import { useEffect, useId, useRef } from "react";
import { motion } from "framer-motion";

let activeModalCount = 0;
let previousScrollbarCompensation = "";

function lockPageScroll() {
  const { body, documentElement } = document;

  if (activeModalCount === 0) {
    previousScrollbarCompensation = body.style.getPropertyValue("--modal-scrollbar-compensation");
    const scrollbarWidth = Math.max(0, window.innerWidth - documentElement.clientWidth);
    body.style.setProperty("--modal-scrollbar-compensation", `${scrollbarWidth}px`);
    documentElement.classList.add("modal-open");
    body.classList.add("modal-open");
  }

  activeModalCount += 1;

  return () => {
    activeModalCount = Math.max(0, activeModalCount - 1);
    if (activeModalCount === 0) {
      documentElement.classList.remove("modal-open");
      body.classList.remove("modal-open");
      if (previousScrollbarCompensation) body.style.setProperty("--modal-scrollbar-compensation", previousScrollbarCompensation);
      else body.style.removeProperty("--modal-scrollbar-compensation");
    }
  };
}

function getFocusableElements(container) {
  return [...container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )].filter((element) => !element.hasAttribute("hidden") && element.getClientRects().length > 0);
}

/** Shared responsive dialog frame for public and admin modals. */
export function ResponsiveModal({ title, description, onClose, children, className = "", testId }) {
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const unlockPageScroll = lockPageScroll();
    const focusTimer = window.setTimeout(() => {
      const initialFocus = dialogRef.current?.querySelector("[data-modal-initial-focus]");
      (initialFocus || dialogRef.current)?.focus({ preventScroll: true });
    }, 0);

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = getFocusableElements(dialogRef.current);
      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);
      unlockPageScroll();
      const returnFocus = returnFocusRef.current;
      if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    };
  }, [onClose]);

  return (
    <motion.div
      className="modal-layer fixed inset-0 z-[150] flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onMouseDown={onClose}
      />
      <motion.section
        ref={dialogRef}
        className={`modal-surface ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        data-testid={testId}
        initial={{ y: 24, opacity: 0, scale: 0.985 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 16, opacity: 0, scale: 0.985 }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
      >
        {title && <span id={titleId} className="sr-only">{title}</span>}
        {description && <span id={descriptionId} className="sr-only">{description}</span>}
        {children}
      </motion.section>
    </motion.div>
  );
}
